import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Input } from "antd"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { SearchGlassIcon } from "components/ui/icons/input/SearchGlassIcon"
import {
	useGetModelNumberDetailsByIds,
	useGetModelNumberImageLinksByIds,
} from "hooks/api/modelNumbers/modelNumbers.hook"
import { useObjectsCategories } from "hooks/api/objectsCategories/objectsCategories.hook"
import { useGetSeries } from "hooks/api/series/series.hook"
import {
	computeMatch,
	findAllModelNumbers,
	findDetectedModelNumbers,
	findModelNumbersForCategory,
	normalizeModelNumber,
} from "helpers/modelNumberMatch.helper"
import { ModelNumberRow } from "./ModelNumberRow"
import type { TFurniture } from "types/elevationReferences.type"
import type { TCategoriesWithModels, TModelNumber } from "types/modelNumbers.type"

import style from "./SelectModelNumberModal.module.scss"

const {
	body,
	section,
	sectionTitle,
	dimensionsRow,
	dimensionLabel,
	unset,
	warning,
	seriesValue,
	list,
	empty,
	footer,
	footerButton,
	searchIcon,
	search,
} = style

type Props = {
	isOpen: boolean
	onClose: () => void
	furniture: TFurniture
	seriesOverrideId: number | null
	initialSelectedId: number | null
	onSave: (model: TModelNumber) => Promise<void>
	isOther?: boolean
}

const renderDimension = (label: string, value: number | null) => (
	<span>
		<span className={dimensionLabel}>{label}</span>
		{value === null ? <span className={unset}>Unset</span> : <span>{value}</span>}
	</span>
)

const tagOrder = (modelTag: "best" | "partial" | "none", isMl: boolean): number => {
	if (modelTag === "best") return 0
	if (isMl) return 1
	if (modelTag === "partial") return 2
	return 3
}

export const SelectModelNumberModal = ({
	isOpen,
	onClose,
	furniture,
	seriesOverrideId,
	initialSelectedId,
	onSave,
	isOther = false,
}: Props) => {
	const { data: categoriesData } = useObjectsCategories()
	const data = categoriesData as unknown as TCategoriesWithModels | undefined
	const { data: seriesList } = useGetSeries()
	const seriesName = useMemo(
		() => seriesList?.find((s) => s.id === seriesOverrideId)?.series_name ?? null,
		[seriesList, seriesOverrideId],
	)
	const [searchTerm, setSearchTerm] = useState("")
	const [selectedId, setSelectedId] = useState<number | null>(initialSelectedId)
	const anchorRef = useRef<HTMLDivElement | null>(null)
	const portalRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (isOpen) {
			setSelectedId(initialSelectedId)
			setSearchTerm("")
		}
	}, [isOpen, initialSelectedId])

	useEffect(() => {
		return () => {
			portalRef.current?.remove()
			portalRef.current = null
		}
	}, [])

	const getContainer = useCallback((): HTMLElement => {
		if (portalRef.current?.isConnected) return portalRef.current
		const root = anchorRef.current?.getRootNode()
		const host: ShadowRoot | HTMLElement =
			root instanceof ShadowRoot
				? root
				: (document.querySelector("apryse-webviewer") as HTMLElement | null)?.shadowRoot ?? document.body
		const container = document.createElement("div")
		container.setAttribute("data-portal", "select-model-number-modal")
		host.appendChild(container)
		portalRef.current = container
		return container
	}, [])

	const categoryId = furniture.category?.id
	const detectedModelNumbers = furniture.detected_model_numbers
	const { width: fWidth, height: fHeight, depth: fDepth } = furniture

	const detectedSet = useMemo(
		() => new Set((detectedModelNumbers ?? []).map(normalizeModelNumber).filter(Boolean)),
		[detectedModelNumbers],
	)

	const baseList = useMemo(() => {
		if (isOther) return findAllModelNumbers(data)
		const categoryList = findModelNumbersForCategory(data, categoryId)
		const detectedList = findDetectedModelNumbers(data, detectedModelNumbers)
		const seenIds = new Set<number>()
		const merged: TModelNumber[] = []
		for (const m of [...categoryList, ...detectedList]) {
			if (seenIds.has(m.id)) continue
			seenIds.add(m.id)
			merged.push(m)
		}
		return merged
	}, [data, categoryId, detectedModelNumbers, isOther])

	const candidates = useMemo(() => {
		const term = searchTerm.trim().toLowerCase()
		const filtered = term ? baseList.filter((m) => m.model_number.toLowerCase().includes(term)) : baseList
		const enriched = filtered.map((model) => {
			const match = computeMatch(model.dimensions, { width: fWidth, height: fHeight, depth: fDepth })
			const isMl = detectedSet.has(normalizeModelNumber(model.model_number))
			return { model, match, isMl, sortKey: tagOrder(match.tag, isMl) }
		})
		return enriched.sort((a, b) => a.sortKey - b.sortKey)
	}, [baseList, detectedSet, searchTerm, fWidth, fHeight, fDepth])

	const candidateIds = useMemo(() => baseList.map((m) => m.id), [baseList])
	const detailQueries = useGetModelNumberDetailsByIds(candidateIds, isOpen)
	// Skip thumbnail prefetch when listing all model numbers (the "Other" category has no
	// own id so the list spans every category). Firing one image-link request per row
	// overwhelms the backend; with a real category the list is small enough to be safe.
	const imageQueries = useGetModelNumberImageLinksByIds(candidateIds, isOpen && !isOther)
	const thumbnailUrlById = useMemo(() => {
		const map = new Map<number, string>()
		imageQueries.forEach((q, idx) => {
			const id = candidateIds[idx]
			const url = q.data?.download_url
			if (id !== undefined && url) map.set(id, url)
		})
		return map
	}, [imageQueries, candidateIds])
	const priceBySeriesId = useMemo(() => {
		const map = new Map<number, boolean>()
		if (seriesOverrideId === null) return map
		detailQueries.forEach((q, idx) => {
			const id = candidateIds[idx]
			if (id === undefined) return
			const data = q.data
			if (!data) return
			const hasPrice = data.pricing_info?.some((p) => p.series.id === seriesOverrideId) ?? false
			map.set(id, hasPrice)
		})
		return map
	}, [detailQueries, candidateIds, seriesOverrideId])

	const hasUnsetDim = furniture.width === null || furniture.height === null || furniture.depth === null
	const canSave =
		selectedId !== null && candidates.some((c) => c.model.id === selectedId)

	const [isSaving, setIsSaving] = useState(false)

	const handleSave = async () => {
		const chosen = candidates.find((c) => c.model.id === selectedId)?.model
		if (!chosen || isSaving) return
		setIsSaving(true)
		try {
			await onSave(chosen)
			onClose()
		} catch {
			// Error already surfaced by the parent (e.g. via toast); keep modal open.
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<>
			<div ref={anchorRef} style={{ display: "none" }} aria-hidden />
			<Modal
				isModalOpen={isOpen}
				setModalClose={onClose}
				width={640}
				title="Select model number"
				getContainer={getContainer}
			>
				<div className={body}>
				<div className={section}>
					<span className={sectionTitle}>Cabinet dimensions</span>
					<div className={dimensionsRow}>
						{renderDimension("W: ", furniture.width)}
						{renderDimension("H: ", furniture.height)}
						{renderDimension("D: ", furniture.depth)}
					</div>
					{hasUnsetDim ? (
						<div className={warning}>Some dimensions are missing — model number matching may be inaccurate.</div>
					) : null}
				</div>

				<div className={section}>
					<span className={sectionTitle}>Series</span>
					{seriesName ? (
						<span className={seriesValue}>{seriesName}</span>
					) : (
						<div className={warning}>No series selected — model number price availability cannot be determined.</div>
					)}
				</div>

				<div className={search}>
					<Input
						placeholder="Search by model number"
						prefix={<SearchGlassIcon className={searchIcon} />}
						size="large"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</div>

				<div className={list}>
					{candidates.length === 0 ? (
						<div className={empty}>No model numbers found.</div>
					) : (
						candidates.map(({ model, match, isMl }) => (
							<ModelNumberRow
								key={model.id}
								model={model}
								isSelected={selectedId === model.id}
								isMlChoice={isMl}
								match={match}
								hasPrice={priceBySeriesId.get(model.id) ?? true}
								thumbnailUrl={thumbnailUrlById.get(model.id) ?? null}
								onSelect={setSelectedId}
							/>
						))
					)}
				</div>

				<div className={footer}>
					<ButtonSecondary type="button" className={footerButton} onClick={onClose}>
						Cancel
					</ButtonSecondary>
					<ButtonPrimary className={footerButton} submittable={canSave && !isSaving} onClick={handleSave}>
						Save
					</ButtonPrimary>
				</div>
				</div>
			</Modal>
		</>
	)
}
